import { useState } from "react";
import { useList, useCreate, useUpdate, useRemove } from "@/hooks/crud";
import { Modal } from "@/components/Modal";
import { TextField, TextAreaField, SelectField } from "@/components/Fields";
import { StatusBadge } from "@/components/Badges";
import { ErrorBanner } from "@/components/Feedback";
import { ASSET_TYPES, ASSET_STATUSES } from "@/lib/enums";
import type { TrackAsset } from "@/types/database";

function AssetForm({
  trackId,
  asset,
  onClose,
}: {
  trackId: number;
  asset: TrackAsset | null;
  onClose: () => void;
}) {
  const create = useCreate("track_assets");
  const update = useUpdate("track_assets");
  const [assetType, setAssetType] = useState(asset?.asset_type ?? "COVER_ART");
  const [tool, setTool] = useState(asset?.tool ?? "");
  const [prompt, setPrompt] = useState(asset?.prompt ?? "");
  const [url, setUrl] = useState(asset?.asset_url ?? "");
  const [status, setStatus] = useState(asset?.status ?? "PENDING");
  const [notes, setNotes] = useState(asset?.notes ?? "");
  const mutation = asset ? update : create;

  async function save() {
    const body = {
      track_id: trackId,
      asset_type: assetType,
      tool: tool.trim() || null,
      prompt: prompt.trim() || null,
      asset_url: url.trim() || null,
      status,
      notes: notes.trim() || null,
    };
    if (asset) await update.mutateAsync({ id: asset.id, body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <Modal
      title={asset ? "Edit Asset" : "New Asset"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {mutation.error && <ErrorBanner error={mutation.error} />}
      <div className="field-row">
        <SelectField label="Type" value={assetType} onChange={setAssetType} options={ASSET_TYPES} />
        <SelectField label="Status" value={status} onChange={setStatus} options={ASSET_STATUSES} />
      </div>
      <TextField label="Tool" value={tool} onChange={setTool} placeholder="Midjourney, Runway…" />
      <TextAreaField label="Prompt" value={prompt} onChange={setPrompt} rows={3} />
      <TextField label="Asset URL" value={url} onChange={setUrl} />
      <TextAreaField label="Notes" value={notes} onChange={setNotes} rows={2} />
    </Modal>
  );
}

export function AssetsPanel({ trackId }: { trackId: number }) {
  const { data } = useList("track_assets", `track_id=eq.${trackId}&order=created_at.desc`);
  const assets = (data?.rows ?? []) as TrackAsset[];
  const remove = useRemove("track_assets");
  const [editing, setEditing] = useState<TrackAsset | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>
          Assets <span className="muted mono">{assets.length}</span>
        </h3>
        <button className="btn btn-sm" onClick={() => setCreating(true)}>
          + Add
        </button>
      </div>
      <div className="panel-body">
        {assets.length === 0 && <span className="muted">No assets.</span>}
        {assets.map((a) => (
          <div
            key={a.id}
            className="card"
            style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", gap: 12 }}
          >
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {a.asset_type} <StatusBadge status={a.status} />
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {a.tool ? `${a.tool}` : "no tool"}
                {a.asset_url && (
                  <>
                    {" · "}
                    <a href={a.asset_url} target="_blank" rel="noreferrer">
                      open
                    </a>
                  </>
                )}
              </div>
              {a.prompt && (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {a.prompt}
                </div>
              )}
            </div>
            <div className="row-actions" style={{ alignItems: "flex-start" }}>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditing(a)}>
                Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (confirm("Delete this asset?")) remove.mutate(a.id);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {creating && <AssetForm trackId={trackId} asset={null} onClose={() => setCreating(false)} />}
      {editing && <AssetForm trackId={trackId} asset={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
